import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, MapPin, Building2, Maximize, CalendarDays, ArrowLeft, Phone, CheckCircle, MessageCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ObjekteDetailPage() {
  const { slug } = useParams();
  const settings = useSettings();
  const [objekt, setObjekt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/objekte/${slug}`)
      .then((r) => {
        setObjekt(r.data);
        document.title = `${r.data.title} | BRIMO Immobilien NRW`;
      })
      .catch(() => setObjekt(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (settings.objekte_enabled === false) return <Navigate to="/" replace />;
  if (loading) return <div className="min-h-screen bg-[#1a1a1a]"><Navbar /><p className="text-gray-500 text-sm pt-32 text-center">Laden...</p></div>;
  if (!objekt) return <div className="min-h-screen bg-[#1a1a1a]"><Navbar /><p className="text-gray-500 text-sm pt-32 text-center">Objekt nicht gefunden.</p><Footer /></div>;

  const isSold = objekt.status === "verkauft";
  const images = objekt.images || [];

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="objekte-detail-page">
      <Navbar />

      <section className="pt-28 pb-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight size={10} />
            <Link to="/objekte" className="hover:text-white transition-colors">Objekte</Link>
            <ChevronRight size={10} />
            <span className="text-white truncate max-w-xs">{objekt.title}</span>
          </nav>
        </div>
      </section>

      <section className="pb-6">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Gallery */}
            <div className="lg:col-span-2">
              {images.length > 0 ? (
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden border border-white/5 mb-3">
                    <img src={images[activeImg]} alt={objekt.title} className="w-full h-full object-cover" data-testid="objekt-main-image" />
                    {isSold && (
                      <div className="absolute top-4 left-4 bg-emerald-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-testid="objekt-sold-banner">
                        <CheckCircle size={14} /> Erfolgreich verkauft
                      </div>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2" data-testid="objekt-gallery-thumbs">
                      {images.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImg(i)}
                          className={`w-20 h-14 flex-shrink-0 overflow-hidden border-2 transition-colors ${
                            i === activeImg ? "border-[#E61D38]" : "border-white/10 hover:border-white/30"
                          }`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-[16/10] bg-[#0d0d0d] border border-white/5 flex items-center justify-center">
                  <Building2 size={48} className="text-gray-700" />
                </div>
              )}
            </div>

            {/* Info Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-[#0d0d0d] border border-white/5 p-6" data-testid="objekt-info-card">
                {isSold && (
                  <div className="bg-emerald-600/10 border border-emerald-500/20 text-emerald-400 text-center py-3 px-4 mb-5 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2" data-testid="objekt-sold-trust">
                    <CheckCircle size={16} /> Erfolgreich vermittelt
                  </div>
                )}

                <h1
                  className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight leading-tight mb-4"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                  data-testid="objekt-title"
                >
                  {objekt.title}
                </h1>

                {objekt.price && (
                  <p className="text-[#E61D38] text-2xl font-black mb-5" style={{ fontFamily: "Montserrat, sans-serif" }} data-testid="objekt-price">
                    {objekt.price}
                  </p>
                )}

                <div className="space-y-3 mb-6">
                  {objekt.district && (
                    <div className="flex items-center gap-3 text-gray-400 text-sm">
                      <MapPin size={14} className="text-[#E61D38] flex-shrink-0" />
                      <span>Kreis Mettmann-{objekt.district}</span>
                    </div>
                  )}
                  {objekt.property_type && (
                    <div className="flex items-center gap-3 text-gray-400 text-sm">
                      <Building2 size={14} className="text-[#E61D38] flex-shrink-0" />
                      <span>{objekt.property_type}</span>
                    </div>
                  )}
                  {objekt.living_area && (
                    <div className="flex items-center gap-3 text-gray-400 text-sm">
                      <Maximize size={14} className="text-[#E61D38] flex-shrink-0" />
                      <span>{objekt.living_area}</span>
                    </div>
                  )}
                  {objekt.year_built && (
                    <div className="flex items-center gap-3 text-gray-400 text-sm">
                      <CalendarDays size={14} className="text-[#E61D38] flex-shrink-0" />
                      <span>Baujahr {objekt.year_built}</span>
                    </div>
                  )}
                </div>

                {!isSold && (
                  <div className="space-y-2.5">
                    <a
                      href={`tel:${cityConfig.phone}`}
                      className="flex items-center justify-center gap-2 w-full bg-[#E61D38] text-white py-3.5 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] transition-colors"
                      data-testid="objekt-cta-call"
                    >
                      <Phone size={14} /> Jetzt anrufen
                    </a>
                    <a
                      href={`https://wa.me/${cityConfig.whatsapp}?text=${encodeURIComponent(`Hallo, ich interessiere mich für: ${objekt.title}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#1a1a1a] border border-white/10 text-white py-3.5 font-bold uppercase tracking-wider text-xs hover:border-[#E61D38]/40 transition-colors"
                      data-testid="objekt-cta-whatsapp"
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                  </div>
                )}

                {/* Agent info */}
                <div className="mt-6 pt-5 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    <img src={cityConfig.manager.photo} alt={cityConfig.manager.name} className="w-11 h-11 rounded-full object-cover border-2 border-[#E61D38]" loading="lazy" />
                    <div>
                      <p className="text-white text-xs font-bold">{cityConfig.manager.name}</p>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider">{cityConfig.manager.title}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <h2 className="text-white font-bold text-lg uppercase tracking-wider mb-4" style={{ fontFamily: "Montserrat, sans-serif" }}>
              Beschreibung
            </h2>
            <div
              className="prose prose-invert prose-sm max-w-none
                [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-lg [&_h2]:mt-8 [&_h2]:mb-3
                [&_h3]:text-white [&_h3]:font-bold [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2
                [&_p]:text-gray-400 [&_p]:leading-relaxed [&_p]:mb-4
                [&_ul]:text-gray-400 [&_ul]:leading-relaxed [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc
                [&_ol]:text-gray-400 [&_ol]:leading-relaxed [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal
                [&_li]:mb-1 [&_strong]:text-white [&_a]:text-[#E61D38]"
              data-testid="objekt-description"
              dangerouslySetInnerHTML={{ __html: objekt.description }}
            />
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      {!isSold && (
        <section className="py-8">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="max-w-2xl">
              <LeadForm
                formType="buyer"
                title="Interesse an diesem Objekt?"
                ctaText="Anfrage senden"
                pageSource={`/objekte/${objekt.slug}`}
              />
            </div>
          </div>
        </section>
      )}

      <section className="py-6">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Link to="/objekte" className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider hover:text-white transition-colors">
            <ArrowLeft size={12} /> Alle Objekte ansehen
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
